import { analytics } from '@/services/analytics';
import type { AnalyticsSurface, InteractionContext, PlayerSurface } from '@/services/analyticsContext';
import { getAudioProgressForBook, getEffectiveProgress } from '@/utils/audioProgressUtils';
import type { Book } from '@/utils/bookRouting';

type BookEventProperties = Record<string, unknown>;

interface PlayerEventContext {
  book: Book;
  progressSeconds: number;
  isPlaying: boolean;
  isConnected: boolean;
  surface: PlayerSurface;
}

interface BookPageViewContext {
  source?: string;
  collectionName?: string;
}

/** How the reader was entered. Replaces the old docked/full `Audio Player View Changed` pair. */
export type ReaderEntrySource =
  | 'book_page'
  | 'my_books'
  | 'docked_player'
  | 'direct_link'
  | 'post_login'
  | 'history';

interface ReaderPageViewContext {
  source: ReaderEntrySource;
  isPlaying: boolean;
  isConnected: boolean;
  /**
   * Book-total position at entry. Omit when the player has not restored this
   * book yet (deep links, cold loads) so the saved progress is used instead.
   */
  progressSeconds?: number;
}

type BookStateSurface = 'my_books' | 'book_page';

export type BookVisibilityTrigger = 'visibility_toggle' | 'playback_start';
export type BookCompletionTrigger = 'manual' | 'playback_completed';

interface BookVisibilityChange extends BookEventProperties {
  hidden: boolean;
  surface: BookStateSurface;
  trigger: BookVisibilityTrigger;
}

interface BookFavouriteStatusChange extends BookEventProperties {
  favourited: boolean;
  surface: BookStateSurface | 'reader';
}

interface BookCompletionStatusChange extends BookEventProperties {
  finished: boolean;
  trigger: BookCompletionTrigger;
  surface: AnalyticsSurface;
}

const roundToOneDecimal = (value: number): number => Number(value.toFixed(1));

const normalizeDuration = (book: Book): number => {
  return Number.isFinite(book.totalDuration) ? Math.max(0, book.totalDuration) : 0;
};

const normalizeProgress = (book: Book, progressSeconds?: number): number => {
  const duration = normalizeDuration(book);
  const savedProgress = getEffectiveProgress(getAudioProgressForBook(book));
  const candidate = progressSeconds ?? savedProgress;
  const finiteProgress = Number.isFinite(candidate) ? Math.max(0, candidate) : 0;

  return duration > 0 ? Math.min(finiteProgress, duration) : 0;
};

class BookAnalytics {
  properties(book: Book, progressSeconds?: number) {
    const duration = normalizeDuration(book);
    const progress = normalizeProgress(book, progressSeconds);
    const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

    return {
      book_id: book.id,
      book_slug: book.slug,
      book_title: book.title,
      book_author: book.author,
      book_featured: book.featured,
      book_collections: book.categories ?? [],
      book_duration: duration,
      book_length_seconds: roundToOneDecimal(duration),
      progress_seconds: roundToOneDecimal(progress),
      progress_percent: roundToOneDecimal(progressPercent),
    };
  }

  track(
    eventName: string,
    book: Book,
    extraProperties: BookEventProperties = {},
    progressSeconds?: number,
  ) {
    analytics.track(eventName, {
      ...extraProperties,
      ...this.properties(book, progressSeconds),
    });
  }

  trackBookPageView(book: Book, context: BookPageViewContext = {}) {
    this.track('Page View', book, {
      view: 'book',
      surface: 'book_page',
      source: context.source,
      collection_name: context.collectionName,
    });
  }

  trackReaderPageView(book: Book, context: ReaderPageViewContext) {
    const { source, isPlaying, isConnected, progressSeconds } = context;

    this.track('Page View', book, {
      view: 'read',
      surface: 'reader',
      source,
      // Named to match trackPlayerEvent so reader entries line up with player events.
      was_playing: isPlaying,
      was_in_conversation: isConnected,
    }, progressSeconds);
  }

  trackVisibilityChange(book: Book, change: BookVisibilityChange) {
    this.track('Book Visibility Changed', book, change);
  }

  trackFavouriteStatusChange(book: Book, change: BookFavouriteStatusChange) {
    this.track('Book Favourite Status Changed', book, change);
  }

  trackCompletionStatusChange(
    book: Book,
    change: BookCompletionStatusChange,
    progressSeconds?: number,
  ) {
    this.track('Book Completion Status Changed', book, change, progressSeconds);
  }

  trackPlayerEvent(
    eventName: string,
    context: PlayerEventContext,
    extraProperties: BookEventProperties = {},
  ) {
    const { book, progressSeconds, isPlaying, isConnected, surface } = context;

    this.track(eventName, book, {
      surface,
      was_playing: isPlaying,
      was_in_conversation: isConnected,
      ...extraProperties,
    }, progressSeconds);
  }

  trackInteraction(
    eventName: string,
    book: Book,
    context: InteractionContext,
    extraProperties: BookEventProperties = {},
    progressSeconds?: number,
  ) {
    this.track(eventName, book, {
      ...context,
      ...extraProperties,
    }, progressSeconds);
  }
}

export const bookAnalytics = new BookAnalytics();
